import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { Search } from "lucide-react";

export const DEITY_CATEGORIES = ["Trimurti", "Shaiva", "Devi · Shakti", "Vaishnava · Dashavatara", "Vaishnava", "Regional", "Navagraha", "Vedic"];

export default function Deities() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  useSeo("Gods & Goddesses of Sanatan Dharma");
  useEffect(() => { api.get("/deities").then((r) => setItems(r.data.items)); }, []);

  const groups = useMemo(() => {
    const f = items.filter((d) => (!cat || d.category === cat) && (!q || d.name.toLowerCase().includes(q.toLowerCase())));
    const cats = [...DEITY_CATEGORIES, ...new Set(f.map((d) => d.category).filter((c) => !DEITY_CATEGORIES.includes(c)))];
    return cats.map((c) => ({ c, list: f.filter((d) => d.category === c) })).filter((g) => g.list.length);
  }, [items, cat, q]);

  return (
    <div data-testid="deities-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">॥ सर्वदेव नमस्कारः ॥</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">Gods & Goddesses</h1>
      <p className="text-[#63534E] mt-5 text-base md:text-lg max-w-2xl leading-relaxed">
        {items.length} deities of Sanatan Dharma: the Trimurti, the Devi, the ten avatars of Vishnu, the Navagraha and much-loved regional deities. Each one has a page with their mantra, story, festivals and temples.
      </p>

      <div className="mt-10 flex flex-col md:flex-row gap-4 md:items-center">
        <div className="relative md:w-72">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#8C6D3B]" />
          <input data-testid="deity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a deity…"
            className="w-full h-11 pl-10 pr-4 rounded-full bg-[#FFFDF9] border border-[#D4AF37]/40 text-sm focus:outline-none focus:border-[#4A0E17]" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["", ...DEITY_CATEGORIES].map((c) => (
            <button key={c || "all"} data-testid={`deity-cat-${c ? c.replace(/[^a-z]/gi, "").toLowerCase() : "all"}`} onClick={() => setCat(c)}
              className={`px-4 h-9 rounded-full text-sm border transition-sacred ${cat === c ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-[#FFFDF9] text-[#4A0E17] border-[#D4AF37]/40"}`}>
              {c || "All"}
            </button>
          ))}
        </div>
      </div>

      {groups.map((g) => (
        <section key={g.c} className="mt-14">
          <h2 className="font-serif text-2xl md:text-3xl text-[#4A0E17] border-b border-[#D4AF37]/40 pb-3">{g.c}</h2>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {g.list.map((d, i) => <DeityCard key={d.slug} d={d} i={i} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function DeityCard({ d, i }) {
  return (
    <Link to={`/deities/${d.slug}`} data-testid={`deity-card-${d.slug}`}
      style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
      className="group fade-up bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden gold-glow transition-sacred block">
      <div className="aspect-square overflow-hidden bg-[#4A0E17]">
        <img src={d.image_url} alt={d.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-4">
        <div className="font-devanagari text-[#8C6D3B] text-sm">{d.sanskrit}</div>
        <div className="font-serif text-lg text-[#4A0E17] leading-tight">{d.name}</div>
        <div className="text-xs text-[#63534E] mt-1 line-clamp-1">{d.title}</div>
      </div>
    </Link>
  );
}
